import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  InputAdornment,
  Tooltip,
  List,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  ContactMail as ContactMailIcon,
  AttachFile as AttachFileIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { SendMessageDto, MessageDetail, Contact, UserSearchResult, SendMessageResponse } from '../../types';
import messageService from '../../services/messageService';
import contactService from '../../services/contactService';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatEmailAddress, extractUsername } from '../../services/systemConfigService';
import RichTextEditor from '../shared/RichTextEditor';
import { sanitizeHtmlForSubmission } from '@shared/richtext/officePaste';
import {
  deleteDraft as deleteLocalDraft,
  type LocalDraftMessage,
  upsertDraft,
} from '../../services/draftMessageStore';

export type ComposeMode = 'reply' | 'replyAll' | 'forward';
export interface InitialComposeRecipient {
  address: string;
  displayName: string;
  maxPay: number;
  deliveryFeeRequired: number;
  type?: 'to' | 'cc' | 'bcc';
}

interface MessageComposerProps {
  onClose: () => void;
  onSent?: () => void;
  onDraftSaved?: () => void;
  replyTo?: MessageDetail;
  composeMode?: ComposeMode;
  initialDraft?: LocalDraftMessage | null;
  initialRecipients?: InitialComposeRecipient[];
}

interface RecipientEntry {
  address: string;
  displayName: string;
  maxPay: number;
  deliveryFeeRequired: number;
  type: 'to' | 'cc' | 'bcc';
}

interface UploadedAttachment {
  tempId: string;
  fileName: string;
  fileSize: number;
  contentType: string;
}

interface ContactsCacheRecord {
  cachedAt: number;
  contacts: Contact[];
}

const CONTACTS_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const getContactsCacheKey = (username: string): string =>
  `compose_contacts_cache:v1:${username.toLowerCase()}`;

const readContactsCache = (username: string): ContactsCacheRecord | null => {
  try {
    const raw = localStorage.getItem(getContactsCacheKey(username));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ContactsCacheRecord;
    if (!Array.isArray(parsed?.contacts) || typeof parsed?.cachedAt !== 'number') {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const writeContactsCache = (username: string, contacts: Contact[]): void => {
  try {
    const payload: ContactsCacheRecord = {
      cachedAt: Date.now(),
      contacts,
    };
    localStorage.setItem(getContactsCacheKey(username), JSON.stringify(payload));
  } catch {
    // Ignore local storage write failures.
  }
};

const MessageComposer: React.FC<MessageComposerProps> = ({
  onClose,
  onSent,
  onDraftSaved,
  replyTo,
  composeMode,
  initialDraft,
  initialRecipients,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  // Recipients state
  const [recipients, setRecipients] = useState<RecipientEntry[]>([]);
  const [recipientType, setRecipientType] = useState<'to' | 'cc' | 'bcc'>('to');
  
  // Message content
  const [subject, setSubject] = useState<string>(() => {
    if (initialDraft) return initialDraft.subject || '';
    if (!replyTo) return '';
    if (composeMode === 'forward') return `Fwd: ${replyTo.subject || ''}`;
    return `Re: ${replyTo.subject || ''}`;
  });
  const [body, setBody] = useState<string>(() => {
    if (initialDraft) return initialDraft.body || '';
    if (!replyTo) return '';
    const dateStr = replyTo.sentDate ? new Date(replyTo.sentDate).toLocaleString() : '';
    if (composeMode === 'forward') {
      const from = replyTo.fromName ? `${replyTo.fromName} &lt;${replyTo.fromAddress}&gt;` : replyTo.fromAddress;
      return `<br><br><div style="border-top:1px solid #ccc;padding-top:10px;margin-top:10px"><p style="color:#555;font-size:0.9em;margin:0 0 8px 0"><strong>---------- Forwarded message ----------</strong><br><strong>From:</strong> ${from}<br><strong>Date:</strong> ${dateStr}<br><strong>Subject:</strong> ${replyTo.subject || '(No Subject)'}</p>${replyTo.body || ''}</div>`;
    }
    // reply / replyAll: quote the original
    const from = replyTo.fromName ? `${replyTo.fromName} &lt;${replyTo.fromAddress}&gt;` : replyTo.fromAddress;
    return `<br><br><blockquote style="border-left:3px solid #ccc;padding-left:12px;margin:0 0 0 8px;color:#555"><p style="font-size:0.9em;margin:0 0 8px 0;color:#555">On ${dateStr}, ${from} wrote:</p>${replyTo.body || ''}</blockquote>`;
  });
  
  // Payment options (defaults loaded from user settings per legacy clsMSRequestGetAccountInfo)
  const [transactionAmount, setTransactionAmount] = useState<string>('0');
  const [maxDeliveryFee, setMaxDeliveryFee] = useState<string>(initialDraft ? String(initialDraft.maxDeliveryFee ?? 0) : '0');
  const [payFees, setPayFees] = useState<boolean>(initialDraft ? !!initialDraft.payFees : false); // Default: recipient pays fees
  const [expirationDays, setExpirationDays] = useState<string>(initialDraft ? String(initialDraft.expirationDays ?? 90) : '90'); // Default, loaded from settings
  
  // UI state
  const [showCc, setShowCc] = useState<boolean>(false);
  const [showBcc, setShowBcc] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pasteTruncatedWarning, setPasteTruncatedWarning] = useState<boolean>(false);
  const [confirmOverPostageOpen, setConfirmOverPostageOpen] = useState<boolean>(false);
  const [confirmPostageMessage, setConfirmPostageMessage] = useState<string>('');
  const [pendingSendDto, setPendingSendDto] = useState<SendMessageDto | null>(null);

  const BODY_SIZE_WARN_BYTES = 10 * 1024 * 1024; // 10 MB
  const bodySizeBytes = new Blob([body]).size;
  const bodyTooLarge = bodySizeBytes > BODY_SIZE_WARN_BYTES;
  
  // Address Book contacts
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loadingContacts, setLoadingContacts] = useState<boolean>(false);
  const requiredPostageMemoRef = useRef<Record<string, number>>({});
  
  // User search dialog
  const [searchDialogOpen, setSearchDialogOpen] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  
  // User delivery settings (defaults from legacy clsMSRequestGetAccountInfo)
  const [defaultMaxPay, setDefaultMaxPay] = useState<number>(0);
  const [_defaultExpirationDays, setDefaultExpirationDays] = useState<number>(90);
  
  // Attachments state
  const [attachments, setAttachments] = useState<UploadedAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draftId, setDraftId] = useState<string | null>(initialDraft?.id ?? null);

  const buildMessageDto = (): SendMessageDto => {
    const toRecipients = recipients.filter(r => r.type === 'to').map(r => r.address);
    const ccRecipients = recipients.filter(r => r.type === 'cc').map(r => r.address);
    const bccRecipients = recipients.filter(r => r.type === 'bcc').map(r => r.address);
    const amount = parseFloat(transactionAmount) || 0;
    const maxFee = parseFloat(maxDeliveryFee) || 0;
    const expiration = parseInt(expirationDays) || 90;
    const cleanBody = sanitizeHtmlForSubmission(body.trim());

    return {
      subject: subject.trim(),
      body: cleanBody,
      toRecipients,
      ccRecipients: ccRecipients.length > 0 ? ccRecipients : undefined,
      bccRecipients: bccRecipients.length > 0 ? bccRecipients : undefined,
      transactionAmount: amount,
      maxDeliveryFee: maxFee,
      payFees,
      expirationDays: expiration,
      attachmentTempIds: attachments.length > 0 ? attachments.map(a => a.tempId) : undefined,
    };
  };

  // Load contacts and user settings on mount
  useEffect(() => {
    loadContacts();
    loadUserSettings();

    if (initialDraft) {
      const draftRecipients: RecipientEntry[] = [
        ...(initialDraft.toRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'to' as const,
        })),
        ...(initialDraft.ccRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'cc' as const,
        })),
        ...(initialDraft.bccRecipients ?? []).map((address) => ({
          address,
          displayName: address,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'bcc' as const,
        })),
      ];
      setRecipients(draftRecipients);
      setShowCc((initialDraft.ccRecipients?.length ?? 0) > 0);
      setShowBcc((initialDraft.bccRecipients?.length ?? 0) > 0);
      setTransactionAmount((initialDraft.transactionAmount ?? 0).toString());
      setMaxDeliveryFee((initialDraft.maxDeliveryFee ?? 0).toString());
      setPayFees(!!initialDraft.payFees);
      setExpirationDays((initialDraft.expirationDays ?? 90).toString());
      return;
    }

    if (initialRecipients && initialRecipients.length > 0) {
      const seededRecipients: RecipientEntry[] = initialRecipients.map((recipient) => ({
        address: recipient.address,
        displayName: recipient.displayName || recipient.address,
        maxPay: Number.isFinite(recipient.maxPay) ? recipient.maxPay : 0,
        deliveryFeeRequired: Number.isFinite(recipient.deliveryFeeRequired) ? recipient.deliveryFeeRequired : 0,
        type: recipient.type ?? 'to',
      }));
      setRecipients(seededRecipients);
      setShowCc(seededRecipients.some((r) => r.type === 'cc'));
      setShowBcc(seededRecipients.some((r) => r.type === 'bcc'));
      return;
    }
    
    // Handle reply / replyAll / forward recipients
    if (replyTo) {
      if (composeMode === 'forward') {
        // Forward: no recipients pre-filled — user chooses where to send
        setRecipients([]);
      } else if (composeMode === 'replyAll') {
        // Reply All: original sender goes To; all other To+CC (excluding self) go CC
        const myAddress = user?.username?.toLowerCase();
        const ccAddresses = [
          ...(replyTo.toRecipients || []),
          ...(replyTo.ccRecipients || []),
        ].filter(addr => {
          const lower = addr.toLowerCase();
          return myAddress && !lower.includes(myAddress);
        });
        const ccEntries = ccAddresses.map(addr => ({
          address: addr,
          displayName: addr,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'cc' as const,
        }));
        setRecipients([
          {
            address: replyTo.fromAddress,
            displayName: replyTo.fromName || replyTo.fromAddress,
            maxPay: 0,
            deliveryFeeRequired: 0,
            type: 'to',
          },
          ...ccEntries,
        ]);
        if (ccEntries.length > 0) setShowCc(true);
      } else {
        // Reply: just the original sender
        setRecipients([{
          address: replyTo.fromAddress,
          displayName: replyTo.fromName || replyTo.fromAddress,
          maxPay: 0,
          deliveryFeeRequired: 0,
          type: 'to',
        }]);
      }
    }
  }, [replyTo, composeMode, initialDraft, initialRecipients, user?.username]);

  async function loadContacts() {
    if (!user?.username) {
      setContacts([]);
      setLoadingContacts(false);
      return;
    }

    const cached = readContactsCache(user.username);
    if (cached?.contacts?.length) {
      setContacts(cached.contacts);
    }

    const isCacheFresh = !!cached && (Date.now() - cached.cachedAt) < CONTACTS_CACHE_TTL_MS;
    if (isCacheFresh) {
      setLoadingContacts(false);
      return;
    }

    setLoadingContacts(true);
    try {
      const response = await api.get<Contact[]>('/api/contacts');
      const normalized = Array.isArray(response) ? response : [];
      setContacts(normalized);
      writeContactsCache(user.username, normalized);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    } finally {
      setLoadingContacts(false);
    }
  }

  const loadUserSettings = async () => {
    try {
      const settings = await api.get<{
        defaultMaxPayAmount: number;
        defaultDaysUntilMailExpires: number;
      }>('/api/contacts/settings');
      
      // Apply user's default settings
      setDefaultMaxPay(settings.defaultMaxPayAmount || 0);
      setMaxDeliveryFee((settings.defaultMaxPayAmount || 0).toString());
      
      // Set expiration days from user's default (legacy: intDefaultDaysMailExpires)
      const expDays = settings.defaultDaysUntilMailExpires || 90;
      setDefaultExpirationDays(expDays);
      setExpirationDays(expDays.toString());
    } catch (err) {
      console.error('Failed to load user settings:', err);
    }
  };

  // Search users with debounce
  const handleSearchUsers = useCallback(async () => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const data = await api.get<UserSearchResult[]>('/api/contacts/search', {
        params: { q: searchTerm }
      });
      setSearchResults(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Search failed:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        handleSearchUsers();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearchUsers]);

  // Add recipient from search result
  const handleAddSearchResult = (user: UserSearchResult) => {
    const address = formatEmailAddress(user.username);
    
    // Check if already added
    if (recipients.some(r => r.address.toLowerCase() === address.toLowerCase())) {
      setError('This recipient has already been added');
      return;
    }
    
    void addRecipientWithLazyPostage({
      address,
      displayName: user.fullName || user.username,
      maxPay: defaultMaxPay,
      deliveryFeeRequired: user.deliveryFeeRequired || 0,
      type: recipientType
    });

    setSearchDialogOpen(false);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Add recipient from contacts
  const handleContactSelect = (contact: Contact | null) => {
    if (contact) {
      // Check if already added
      if (recipients.some(r => r.address.toLowerCase() === contact.address.toLowerCase())) {
        setError('This recipient has already been added');
        return;
      }
      
      void addRecipientWithLazyPostage({
        address: contact.address,
        displayName: contact.displayName,
        maxPay: contact.maxPay || defaultMaxPay,
        deliveryFeeRequired: contact.deliveryRequired || 0,
        type: recipientType
      });
    }
  };

  const updateExpectedPostageInContacts = useCallback((address: string, requiredPostage: number) => {
    const normalizedAddress = (address || '').trim().toLowerCase();
    if (!normalizedAddress) return;

    setContacts((prev) => {
      let changed = false;
      const updated = prev.map((contact) => {
        if ((contact.address || '').trim().toLowerCase() !== normalizedAddress) return contact;
        if (Math.abs((contact.deliveryRequired || 0) - requiredPostage) < 0.0001) return contact;
        changed = true;
        return { ...contact, deliveryRequired: requiredPostage };
      });

      if (!changed) return prev;
      if (user?.username) {
        writeContactsCache(user.username, updated);
      }
      return updated;
    });
  }, [user?.username]);

  const getLazyRequiredPostage = useCallback(async (address: string, fallback: number): Promise<number> => {
    const recipientUsername = extractUsername(address);
    if (!recipientUsername) return fallback;
    const key = recipientUsername.trim().toLowerCase();

    if (typeof requiredPostageMemoRef.current[key] === 'number') {
      return requiredPostageMemoRef.current[key];
    }

    try {
      const result = await contactService.getRequiredPostage(recipientUsername);
      const requiredPostage = Number(result?.requiredPostage);
      if (Number.isFinite(requiredPostage) && requiredPostage >= 0) {
        requiredPostageMemoRef.current[key] = requiredPostage;
        return requiredPostage;
      }
      return fallback;
    } catch {
      return fallback;
    }
  }, []);

  const addRecipientWithLazyPostage = useCallback(async (entry: RecipientEntry) => {
    const fallbackPostage = Number.isFinite(entry.deliveryFeeRequired) ? entry.deliveryFeeRequired : 0;
    const requiredPostage = await getLazyRequiredPostage(entry.address, fallbackPostage);

    setRecipients((prev) => {
      if (prev.some((recipient) => recipient.address.toLowerCase() === entry.address.toLowerCase())) {
        return prev;
      }
      return [...prev, { ...entry, deliveryFeeRequired: requiredPostage }];
    });

    if (Math.abs(requiredPostage - fallbackPostage) >= 0.0001) {
      updateExpectedPostageInContacts(entry.address, requiredPostage);
    }
  }, [getLazyRequiredPostage, updateExpectedPostageInContacts]);

  // Remove recipient
  const handleRemoveRecipient = (address: string) => {
    setRecipients(prev => prev.filter(r => r.address !== address));
  };

  // Upload an image file and return its URL for inline embedding in the editor body
  const uploadInlineImage = useCallback(async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('files', file);
    try {
      const response = await api.post<UploadedAttachment[]>('/api/Attachment/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (Array.isArray(response) && response.length > 0) {
        const uploaded = response[0];
        // Build the URL to the attachment so TipTap can insert it as <img src="...">
        return `/api/Attachment/${uploaded.tempId}`;
      }
      throw new Error('No file returned from server');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload image');
      throw err;
    }
  }, []);

  // Handle file selection for attachments
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);
    setError(null);

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }

      const response = await api.post<UploadedAttachment[]>('/api/Attachment/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (Array.isArray(response)) {
        setAttachments(prev => [...prev, ...response]);
      }
    } catch (err: any) {
      console.error('Upload failed:', err);
      setError(err.response?.data?.error || 'Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Remove an uploaded attachment
  const handleRemoveAttachment = async (tempId: string) => {
    try {
      await api.delete(`/api/Attachment/temp/${encodeURIComponent(tempId)}`);
      setAttachments(prev => prev.filter(a => a.tempId !== tempId));
    } catch (err: any) {
      console.error('Failed to remove attachment:', err);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };


  const validateRecipients = (): boolean => {
    const toRecipients = recipients.filter(r => r.type === 'to');
    if (toRecipients.length === 0) {
      setError('Please add at least one recipient');
      return false;
    }

    return true;
  };

  const sendMessageDto = async (messageDto: SendMessageDto): Promise<SendMessageResponse> => {
    return await messageService.sendMessage(messageDto);
  };

  const handleSaveDraft = () => {
    if (!user?.username) {
      setError('Unable to save draft: not authenticated');
      return;
    }

    const payload = buildMessageDto();
    const hasAnyContent = !!(
      payload.subject?.trim() ||
      payload.body?.trim() ||
      payload.toRecipients.length ||
      payload.ccRecipients?.length ||
      payload.bccRecipients?.length
    );
    if (!hasAnyContent) {
      setError('Add at least a recipient, subject, or message body before saving draft');
      return;
    }

    const saved = upsertDraft(user.username, payload, draftId ?? undefined);
    setDraftId(saved.id);
    setSuccess('Draft saved');
    onDraftSaved?.();
  };

  const handleSend = async () => {
    setError(null);
    setSuccess(null);

    if (!validateRecipients()) {
      return;
    }

    if (!subject.trim()) {
      if (!window.confirm('Send message without a subject?')) {
        return;
      }
    }

    const amount = parseFloat(transactionAmount);
    if (isNaN(amount) || amount < 0) {
      setError('Invalid transaction amount');
      return;
    }

    const expiration = parseInt(expirationDays);
    if (isNaN(expiration) || expiration < 1 || expiration > 365) {
      setError('Expiration days must be between 1 and 365');
      return;
    }

    const maxFee = parseFloat(maxDeliveryFee);
    if (isNaN(maxFee) || maxFee < 0) {
      setError('Invalid maximum delivery fee');
      return;
    }

    setSending(true);

    try {
      const messageDto: SendMessageDto = buildMessageDto();

      console.log('Sending message:', messageDto);
      console.log('Body length:', messageDto.body.length);
      console.log('Body preview:', messageDto.body.substring(0, 200));
      const response = await sendMessageDto(messageDto);
      if (!response.success) {
        setError(response.message || 'Failed to send message');
        return;
      }
      setSuccess(response.message || 'Message queued for delivery');

      if (draftId && user?.username) {
        deleteLocalDraft(user.username, draftId);
        setDraftId(null);
      }
      
      // Invalidate balance cache to trigger refresh
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      
      // Close composer after 2 seconds
      setTimeout(() => {
        if (onSent) {
          onSent();
        }
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('Send message error:', err);
      const status = err?.response?.status;
      const data = err?.response?.data as SendMessageResponse | undefined;
      if (status === 409 && data?.requiresConfirmation) {
        setPendingSendDto(messageDto);
        setConfirmPostageMessage(data.message || 'One or more recipients require postage above your alert threshold.');
        setConfirmOverPostageOpen(true);
        return;
      }
      const errorMsg = err.response?.data?.message || err.message || 'Failed to send message';
      setError(errorMsg);
    } finally {
      setSending(false);
    }
  };

  const handleConfirmOverPostageSend = async () => {
    if (!pendingSendDto) {
      setConfirmOverPostageOpen(false);
      return;
    }

    setConfirmOverPostageOpen(false);
    setSending(true);
    setError(null);

    try {
      const response = await sendMessageDto({
        ...pendingSendDto,
        confirmOverPostageAlert: true
      });
      if (!response.success) {
        setError(response.message || 'Failed to send message');
        return;
      }

      setSuccess(response.message || 'Message queued for delivery');
      if (draftId && user?.username) {
        deleteLocalDraft(user.username, draftId);
        setDraftId(null);
      }
      queryClient.invalidateQueries({ queryKey: ['accountBalance', user?.username] });
      setTimeout(() => {
        if (onSent) {
          onSent();
        }
        onClose();
      }, 2000);
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to send message';
      setError(errorMsg);
    } finally {
      setPendingSendDto(null);
      setSending(false);
    }
  };

  const getRecipientsByType = (type: 'to' | 'cc' | 'bcc') => 
    recipients.filter(r => r.type === type);

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6">
          {composeMode === 'forward' ? 'Forward Message' : composeMode === 'replyAll' ? 'Reply All' : replyTo ? 'Reply to Message' : 'Compose Message'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Form */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        {/* Recipients Section */}
        <Box sx={{ mb: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ContactMailIcon fontSize="small" />
            Recipients
          </Typography>
          
          {/* Add from contacts or search */}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Autocomplete
              options={contacts}
              getOptionLabel={(option) => `${option.displayName} <${option.address}>`}
              loading={loadingContacts}
              onChange={(_, value) => handleContactSelect(value)}
              sx={{ flexGrow: 1 }}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select from Address Book..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <ContactMailIcon fontSize="small" color="action" />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.contactID}>
                  <Box>
                    <Typography variant="body2">{option.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.address}
                      {option.deliveryRequired > 0 && (
                        <Chip 
                          label={`Requires $${option.deliveryRequired.toFixed(2)}`} 
                          size="small" 
                          color="info"
                          sx={{ ml: 1, height: 16, fontSize: '0.65rem' }}
                        />
                      )}
                    </Typography>
                  </Box>
                </li>
              )}
            />
            
            <Button
              variant="outlined"
              startIcon={<SearchIcon />}
              onClick={() => setSearchDialogOpen(true)}
              size="small"
            >
              Find User
            </Button>
          </Stack>
          
          {/* Recipient type toggle */}
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">
              Add to:
            </Typography>
            <Chip 
              label="To" 
              color={recipientType === 'to' ? 'primary' : 'default'}
              onClick={() => setRecipientType('to')}
              size="small"
            />
            <Chip 
              label="CC" 
              color={recipientType === 'cc' ? 'primary' : 'default'}
              onClick={() => { setRecipientType('cc'); setShowCc(true); }}
              size="small"
            />
            <Chip 
              label="BCC" 
              color={recipientType === 'bcc' ? 'primary' : 'default'}
              onClick={() => { setRecipientType('bcc'); setShowBcc(true); }}
              size="small"
            />
          </Stack>

          {/* To Recipients */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
            >
              To:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
              {getRecipientsByType('to').map(r => (
                <Tooltip key={r.address} title={`Current required postage: $${r.deliveryFeeRequired.toFixed(2)}`}>
                  <Chip
                    label={r.displayName}
                    onDelete={() => handleRemoveRecipient(r.address)}
                    size="small"
                    color="default"
                  />
                </Tooltip>
              ))}
              {getRecipientsByType('to').length === 0 && (
                <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                  No recipients added
                </Typography>
              )}
            </Box>
          </Box>

          {/* CC Recipients */}
          {(showCc || getRecipientsByType('cc').length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
              >
                CC:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
                {getRecipientsByType('cc').map(r => (
                  <Chip
                    key={r.address}
                    label={r.displayName}
                    onDelete={() => handleRemoveRecipient(r.address)}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {getRecipientsByType('cc').length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                    None
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* BCC Recipients */}
          {(showBcc || getRecipientsByType('bcc').length > 0) && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ minWidth: 34, lineHeight: '24px', fontWeight: 600 }}
              >
                BCC:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1, minHeight: 24 }}>
                {getRecipientsByType('bcc').map(r => (
                  <Chip
                    key={r.address}
                    label={r.displayName}
                    onDelete={() => handleRemoveRecipient(r.address)}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {getRecipientsByType('bcc').length === 0 && (
                  <Typography variant="caption" color="text.disabled" sx={{ lineHeight: '24px' }}>
                    None
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>

        {/* Subject Field */}
        <TextField
          fullWidth
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={sending}
          sx={{ mb: 2 }}
        />

        {/* Body Field - Rich Text Editor */}
        <Box sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <RichTextEditor
            value={body}
            onChange={setBody}
            disabled={sending}
            placeholder="Type your message here..."
            minHeight={260}
            maxBodyHeight="min(52vh, 420px)"
            onPasteTruncated={() => setPasteTruncatedWarning(true)}
            uploadImageFile={uploadInlineImage}
          />
        </Box>

        {/* Paste truncation notice */}
        {pasteTruncatedWarning && (
          <Alert
            severity="warning"
            onClose={() => setPasteTruncatedWarning(false)}
            sx={{ mb: 1 }}
          >
            Pasted content was too large and has been trimmed (limit: 2 MB of raw HTML). You can paste the remaining sections separately to continue building your message.
          </Alert>
        )}

        {/* Body size advisory */}
        {bodyTooLarge && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Your message is getting large (~{Math.round(bodySizeBytes / (1024 * 1024))} MB). Consider moving images to attachments for faster delivery.
          </Alert>
        )}

        

        {/* Attachments Section */}
        <Box sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachFileIcon fontSize="small" />
            Attachments
          </Typography>

          {/* File input (hidden) */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            style={{ display: 'none' }}
          />

          {/* Add attachment button */}
          <Button
            variant="outlined"
            startIcon={uploadingAttachment ? <CircularProgress size={16} /> : <AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending || uploadingAttachment}
            size="small"
            sx={{ mb: 1 }}
          >
            {uploadingAttachment ? 'Uploading...' : 'Add Attachment'}
          </Button>

          {/* Attachment list */}
          {attachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {attachments.map((attachment) => (
                <Chip
                  key={attachment.tempId}
                  icon={<AttachFileIcon />}
                  label={`${attachment.fileName} (${formatFileSize(attachment.fileSize)})`}
                  onDelete={() => handleRemoveAttachment(attachment.tempId)}
                  deleteIcon={<DeleteIcon />}
                  sx={{ m: 0.5 }}
                  variant="outlined"
                />
              ))}
            </Box>
          )}

          {attachments.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No attachments. Max 5MB per file.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Payment & Delivery Options */}
        <Typography variant="subtitle2" gutterBottom>
          Payment & Delivery Options
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Tooltip title="Amount to send with this message (paid on delivery)">
            <TextField
              label="Attached Amount ($)"
              type="text"
              value={transactionAmount}
              onChange={(e) => {
                // Only allow numbers and decimal points
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setTransactionAmount(val);
              }}
              onBlur={(e) => {
                // Format to 2 decimal places on blur
                const num = parseFloat(e.target.value) || 0;
                setTransactionAmount(num.toFixed(2));
              }}
              disabled={sending}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: '170px' }}
              helperText="Amount paid on delivery"
              size="small"
            />
          </Tooltip>

          <Tooltip title="Show a warning confirmation if recipient postage exceeds this amount">
            <TextField
              label="Alert Me If Postage Is Over ($)"
              type="text"
              value={maxDeliveryFee}
              onChange={(e) => {
                // Only allow numbers and decimal points
                const val = e.target.value.replace(/[^0-9.]/g, '');
                setMaxDeliveryFee(val);
              }}
              onBlur={(e) => {
                // Format to 2 decimal places on blur
                const num = parseFloat(e.target.value) || 0;
                setMaxDeliveryFee(num.toFixed(2));
              }}
              disabled={sending}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{ width: '170px' }}
              helperText="Used for warning/confirmation only"
              size="small"
            />
          </Tooltip>

          <Tooltip title="Message will expire after this many days if not delivered">
            <TextField
              label="Expires After (days)"
              type="number"
              value={expirationDays}
              onChange={(e) => setExpirationDays(e.target.value)}
              disabled={sending}
              inputProps={{ min: 1, max: 365 }}
              sx={{ width: '140px' }}
              helperText="Message expiration"
              size="small"
            />
          </Tooltip>
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={payFees}
              onChange={(e) => setPayFees(e.target.checked)}
              disabled={sending}
            />
          }
          label="Sender pays transaction fees"
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Message Queue:</strong> Your message will be queued for delivery. 
            You can cancel undelivered messages from your Sent folder.
            Messages expire after {expirationDays} day(s) if not delivered.
          </Typography>
        </Alert>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {recipients.length} recipient(s){attachments.length > 0 && `, ${attachments.length} attachment(s)`}
        </Typography>

        <Box display="flex" gap={1}>
          <Button onClick={onClose} disabled={sending}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveDraft}
            disabled={sending}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
          >
            {sending ? 'Sending...' : 'Send'}
          </Button>
        </Box>
      </Box>

      {/* Error/Success Messages - Below Send Button */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mx: 2, mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* User Search Dialog */}
      <Dialog open={searchDialogOpen} onClose={() => setSearchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" />
          Find Qixit User
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search for Qixit users by username or name. The contact's required delivery fee is shown.
          </Typography>
          
          <TextField
            fullWidth
            placeholder="Search by username or name (minimum 2 characters)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              endAdornment: searchLoading ? <CircularProgress size={20} /> : null,
            }}
            sx={{ mb: 2 }}
          />

          {searchResults.length > 0 && (
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {searchResults.map((user) => (
                  <ListItemButton 
                    key={user.username}
                    onClick={() => handleAddSearchResult(user)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography fontWeight="bold">{user.username}</Typography>
                          {!user.isActive && (
                            <Chip label="Inactive" size="small" color="default" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption">{user.fullName}</Typography>
                          <Chip
                            label={`Requires $${(user.deliveryFeeRequired || 0).toFixed(2)}`}
                            size="small"
                            color="info"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          {searchTerm.length >= 2 && searchResults.length === 0 && !searchLoading && (
            <Alert severity="info">
              No users found matching "{searchTerm}"
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSearchDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOverPostageOpen} onClose={() => setConfirmOverPostageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Postage Alert Confirmation</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {confirmPostageMessage || 'One or more recipients require postage above your alert threshold.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverPostageOpen(false)} disabled={sending}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleConfirmOverPostageSend} disabled={sending}>
            Send Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default MessageComposer;
